import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useDefenderIdentity } from "../contexts/FeatureFlagsContext";
import { providerLabel, identityDisplayName, identityDeepLink } from "./IdentityInventoryPanel";
import { deviceDeepLink } from "./DeviceInventoryPanel";

/**
 * The risk detail's "Affected <type>" tab (Defender for Identity Phase 2).
 *
 * For User Accounts on a risk that has identity links, the rows come from the links: matched
 * identities render with their inventory fields, unmatched ones as the name the finding gave.
 * Everything else — devices, Azure resources, and any risk without links (synced before Phase 2,
 * or the flag off) — renders the prose-parsed grid exactly as before. The prose parser is a
 * fallback, never removed.
 *
 * @param riskId            the UnifiedRisk id
 * @param affectedResources parseAffectedResources(risk.riskDescription) — { typeLabel, names, total }
 */
export const MATCH_KIND_LABELS = {
  ObjectId:    "matched by object id",
  Sid:         "matched by SID",
  Upn:         "matched by UPN",
  AccountName: "matched by account name",
  Email:       "matched by email",
  None:        "not in inventory",
};

export default function RiskAffectedIdentities({ riskId, affectedResources }) {
  const apiCall = useApi();
  const flag = useDefenderIdentity();
  const isUsers = affectedResources?.typeLabel === "User Accounts";
  // Phase 5: the same for device-typed prose lists ("Devices", "Devices & Entities", "Entities").
  const isDevices = !isUsers && /device|entit/i.test(affectedResources?.typeLabel || "");
  const [links, setLinks] = useState(null);   // null = not asked / not applicable
  const [deviceLinks, setDeviceLinks] = useState(null);

  useEffect(() => {
    if (!flag || !isUsers || !riskId) { setLinks(null); return; }
    let cancelled = false;
    apiCall("GET", `/defender-identity/risks/${encodeURIComponent(riskId)}/identities`)
      .then(rows => { if (!cancelled) setLinks(Array.isArray(rows) ? rows : null); })
      .catch(() => { if (!cancelled) setLinks(null); });   // 404 (flag off / unreadable) → prose
    return () => { cancelled = true; };
  }, [apiCall, flag, isUsers, riskId]);

  useEffect(() => {
    if (!flag || !isDevices || !riskId) { setDeviceLinks(null); return; }
    let cancelled = false;
    apiCall("GET", `/defender-identity/risks/${encodeURIComponent(riskId)}/devices`)
      .then(rows => { if (!cancelled) setDeviceLinks(Array.isArray(rows) ? rows : null); })
      .catch(() => { if (!cancelled) setDeviceLinks(null); });
    return () => { cancelled = true; };
  }, [apiCall, flag, isDevices, riskId]);

  const useLinks = isUsers && links && links.length > 0;
  const useDeviceLinks = isDevices && deviceLinks && deviceLinks.length > 0;

  if (useDeviceLinks) return <AffectedDevices links={deviceLinks} />;

  if (!useLinks) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-900">Affected {affectedResources.typeLabel}</h4>
          <span className="text-xs text-gray-500">{affectedResources.total} total · {affectedResources.names.length} named</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {affectedResources.names.map(name => (
            <div key={name} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-orange-50 border border-orange-100 rounded text-xs text-gray-800 truncate" title={name}>
              <span className="flex-shrink-0">💻</span>{name}
            </div>
          ))}
        </div>
        {affectedResources.total > affectedResources.names.length && (
          <p className="text-xs text-gray-400 mt-2">
            The source reports {affectedResources.total - affectedResources.names.length} more affected {affectedResources.typeLabel.toLowerCase()} than it named.
          </p>
        )}
      </div>
    );
  }

  const active = links.filter(l => l.isActive);
  const inactive = links.filter(l => !l.isActive);
  const matched = active.filter(l => l.identityAssetId != null).length;

  return (
    <div data-testid="risk-affected-identities">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">Affected identities</h4>
        <span className="text-xs text-gray-500">
          {active.length} named · {matched} in inventory{inactive.length > 0 ? ` · ${inactive.length} no longer named` : ""}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {[...active, ...inactive].map(l => (
          <div key={l.linkId}
            className={`px-2.5 py-2 rounded border text-xs ${l.isActive ? (l.identityAssetId != null ? "bg-indigo-50 border-indigo-100" : "bg-gray-50 border-gray-200") : "bg-white border-dashed border-gray-200 text-gray-400"}`}>
            <div className="flex items-center justify-between gap-2">
              {l.identityAssetId != null
                ? <a href={identityDeepLink(l.identityAssetId)} className="font-medium text-gray-900 hover:underline truncate" title="Open in the identity inventory">{identityDisplayName(l)}</a>
                : <span className="font-medium text-gray-900 truncate">{l.unmatchedName || "unknown"}</span>}
              <span className="flex flex-wrap gap-1 flex-shrink-0">
                {l.isPrivileged && <span className="px-1 rounded bg-rose-50 text-rose-700 border border-rose-200">Privileged</span>}
                {l.isEnabled === false && <span className="px-1 rounded bg-gray-100 text-gray-600 border border-gray-200">Disabled</span>}
                {l.identityAssetId != null && !l.seenInLastSync && <span className="px-1 rounded bg-amber-50 text-amber-700 border border-amber-200">Not in last sync</span>}
              </span>
            </div>
            <div className="text-gray-500 mt-0.5">
              {l.identityAssetId != null
                ? <>{l.accountUpn || l.accountName || ""}{l.accountUpn || l.accountName ? " · " : ""}{providerLabel(l)}</>
                : "Not in the identity inventory"}
              {" · "}<span className="text-gray-400">{MATCH_KIND_LABELS[l.matchKind] || l.matchKind}</span>
              {!l.isActive && <span className="text-gray-400"> · no longer named by this finding</span>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">
        Identities are linked from the finding's own entities and shown as context. No risk is created or changed by a link.
      </p>
    </div>
  );
}


/** Phase 5: device links as objects (matched rows deep-link to the device drawer), unmatched names in plain text. */
function AffectedDevices({ links }) {
  const active = links.filter(l => l.isActive);
  const inactive = links.filter(l => !l.isActive);
  const matched = active.filter(l => l.deviceAssetId != null).length;
  return (
    <div data-testid="risk-affected-devices">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">Affected devices</h4>
        <span className="text-xs text-gray-500">{active.length} named · {matched} in inventory{inactive.length > 0 ? ` · ${inactive.length} no longer named` : ""}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {[...active, ...inactive].map(l => (
          <div key={l.linkId} className={`px-2.5 py-2 rounded border text-xs ${l.isActive ? (l.deviceAssetId != null ? "bg-indigo-50 border-indigo-100" : "bg-gray-50 border-gray-200") : "bg-white border-dashed border-gray-200 text-gray-400"}`}>
            <div className="flex items-center justify-between gap-2">
              {l.deviceAssetId != null
                ? <a href={deviceDeepLink(l.deviceAssetId)} className="font-medium text-gray-900 hover:underline truncate" title="Open in the device inventory">{l.displayName || l.dnsName}</a>
                : <span className="font-medium text-gray-900 truncate">{l.unmatchedName || "unknown"}</span>}
              <span className="flex flex-wrap gap-1 flex-shrink-0">
                {l.isDomainController && <span className="px-1 rounded bg-rose-50 text-rose-700 border border-rose-200">Domain controller</span>}
                {l.riskScore && l.riskScore !== "None" && <span className="px-1 rounded bg-orange-50 text-orange-700 border border-orange-200">{l.riskScore} risk</span>}
                {l.deviceAssetId != null && !l.seenInLastSync && <span className="px-1 rounded bg-amber-50 text-amber-700 border border-amber-200">Not in last sync</span>}
              </span>
            </div>
            <div className="text-gray-500 mt-0.5">
              {l.deviceAssetId != null ? <>{l.dnsName || ""}{l.osPlatform ? ` · ${l.osPlatform}` : ""}{l.healthStatus ? ` · ${l.healthStatus}` : ""}</> : "Not in the device inventory"}
              {" · "}<span className="text-gray-400">matched by {l.matchKind}</span>
              {!l.isActive && <span className="text-gray-400"> · no longer named by this finding</span>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Devices are linked from the finding's own entities and shown as context. No risk is created or changed by a link.</p>
    </div>
  );
}
